import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Alert,
} from "@mui/material";
import { Add, Delete, Edit, Refresh } from "@mui/icons-material";
import { toast } from "react-toastify";
import EstadoFormDialog from "../../../../components/EstadoFormDialog"; // deja tu path tal cual lo tengas
import { EstadoReserva, deleteEstado, listEstados } from "../../../../api/estadosReserva";

function ColorSwatch({ color }: { color?: string | null }) {
  if (!color) return <span>—</span>;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "4px",
          border: "1px solid #ccc",
          background: color,
        }}
      />
      <Typography variant="body2">{color}</Typography>
    </Stack>
  );
}

export default function EstadosReservaPage() {
  const [rows, setRows] = useState<EstadoReserva[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  // 🔧 por defecto mostramos TODO para evitar “lista vacía”
  const [mostrarOcultos, setMostrarOcultos] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<EstadoReserva | null>(null);

  const [confirmCode, setConfirmCode] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await listEstados();
      // debug opcional
      setRows(
        data.sort((a, b) =>
          a.orden === b.orden ? a.descripcion.localeCompare(b.descripcion) : a.orden - b.orden
        )
      );
    } catch (e: any) {
      toast.error(e?.message || "No se pudo cargar la lista de estados");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();

    return rows.filter((r) => {
      // 🔧 Filtrado de ocultos/internos: si el switch está apagado, oculta
      // los estados no visibles O internos. Con el switch encendido, no oculta nada.
      if (!mostrarOcultos && (r.systemFlag || !r.visibleCliente)) return false;

      if (!q) return true;
      return (
        r.code.toLowerCase().includes(q) ||
        r.descripcion.toLowerCase().includes(q) ||
        r.categoria.toLowerCase().includes(q)
      );
    });
  }, [rows, query, mostrarOcultos]);

  function openCreate() {
    setEditRow(null);
    setFormOpen(true);
  }
  function openEdit(r: EstadoReserva) {
    setEditRow(r);
    setFormOpen(true);
  }

  async function onDelete(code: string) {
    try {
      await deleteEstado(code);
      toast.success("Estado eliminado (ocultado)");
      setConfirmCode(null);
      load();
    } catch (e: any) {
      toast.error(e?.message || "No se pudo eliminar");
    }
  }

  return (
    <Box p={{ xs: 1, md: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Typography variant="h5">Estados de reserva</Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar… (code, descripción, categoría)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Mostrar ocultos</Typography>
            <Switch
              checked={mostrarOcultos}
              onChange={(e) => setMostrarOcultos(e.target.checked)}
            />
          </Stack>
          <Tooltip title="Refrescar">
            <span>
              <IconButton onClick={load} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : <Refresh />}
              </IconButton>
            </span>
          </Tooltip>
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
            Nuevo
          </Button>
        </Stack>
      </Stack>

      {!loading && rows && rows.length > 0 && filtered.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          No hay resultados con el filtro aplicado.
          {!mostrarOcultos && ' Activa "Mostrar ocultos" para ver estados internos/no visibles.'}
        </Alert>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Orden</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell align="center">Bloqueante</TableCell>
                <TableCell align="center">Cuenta horas</TableCell>
                <TableCell align="center">Req. comentario</TableCell>
                <TableCell align="center">Visible cliente</TableCell>
                <TableCell>Color</TableCell>
                <TableCell align="center">Interno</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!rows && (
                <TableRow>
                  <TableCell colSpan={12}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography>Cargando…</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {rows && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12}>
                    <Typography variant="body2" color="text.secondary">
                      No hay resultados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.code} hover>
                  <TableCell width={80}>{r.orden}</TableCell>
                  <TableCell width={180}>
                    <Typography fontFamily="monospace">{r.code}</Typography>
                  </TableCell>
                  <TableCell>{r.descripcion}</TableCell>
                  <TableCell width={120}>
                    <Chip
                      size="small"
                      label={r.categoria}
                      color={
                        r.categoria === "OK"
                          ? "success"
                          : r.categoria === "CANCEL"
                            ? "error"
                            : r.categoria === "PENDIENTE"
                              ? "warning"
                              : r.categoria === "BLOQUEO"
                                ? "default"
                                : "info"
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell width={130}>{r.actorCancelacion ?? "—"}</TableCell>
                  <TableCell align="center">{r.bloqueante ? "Sí" : "No"}</TableCell>
                  <TableCell align="center">{r.cuentaHoras ? "Sí" : "No"}</TableCell>
                  <TableCell align="center">{r.requiereComentario ? "Sí" : "No"}</TableCell>
                  <TableCell align="center">
                    {r.visibleCliente ? (
                      <Chip size="small" label="Sí" color="success" variant="outlined" />
                    ) : (
                      <Chip size="small" label="No" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell width={160}>
                    <ColorSwatch color={r.color} />
                  </TableCell>
                  <TableCell align="center">
                    {r.systemFlag ? <Chip size="small" label="Sí" variant="outlined" /> : "—"}
                  </TableCell>
                  <TableCell align="right" width={120}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEdit(r)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar (ocultar)">
                      <IconButton size="small" onClick={() => setConfirmCode(r.code)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialogo crear/editar */}
      <EstadoFormDialog
        open={formOpen}
        initial={editRow}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          setEditRow(null);
          load(); // refetch para evitar inconsistencias si cambió el code
        }}
      />

      {/* Confirmación delete */}
      <Dialog open={!!confirmCode} onClose={() => setConfirmCode(null)}>
        <DialogTitle>Eliminar estado</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres eliminar (ocultar) el estado <strong>{confirmCode}</strong>?<br />
            <em>Esto marca visibleCliente=0 y systemFlag=1 en backend (borrado lógico).</em>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCode(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => confirmCode && onDelete(confirmCode)}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
